import React, { useState, useCallback, useEffect } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import Image from 'next/image'
import { firstUpper } from './utils'
import ImagePattern from './ImagePattern'
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'

interface BeforeAfterGalleryProps {
	patient: {
		title: string
		history: string
		photos: {
			directus_files_id: {
				id: string
				filename_disk: string
			}
		}[]
	}
}

export default function BeforeAfterGallery({
	patient,
}: BeforeAfterGalleryProps) {
	const [selectedIndex, setSelectedIndex] = useState(0)
	const [isMobile, setIsMobile] = useState(false)
	const [mainCarouselRef, mainCarouselApi] = useEmblaCarousel({ 
		loop: true
	})
	const [imageHeights, setImageHeights] = useState<{[key: number]: number}>({})

	// Check if device is mobile
	useEffect(() => {
		const checkIfMobile = () => {
			setIsMobile(window.innerWidth < 768)
		}
		
		checkIfMobile()
		window.addEventListener('resize', checkIfMobile)
		
		return () => {
			window.removeEventListener('resize', checkIfMobile)
		}
	}, [])

	const onSelect = useCallback(() => {
		if (!mainCarouselApi) return
		setSelectedIndex(mainCarouselApi.selectedScrollSnap())
	}, [mainCarouselApi])

	useEffect(() => {
		if (!mainCarouselApi) return

		onSelect()
		mainCarouselApi.on('select', onSelect)

		return () => {
			mainCarouselApi.off('select', onSelect)
		}
	}, [mainCarouselApi, onSelect])

	const scrollPrev = useCallback(() => {
		if (mainCarouselApi && !isMobile) mainCarouselApi.scrollPrev()
	}, [mainCarouselApi, isMobile])

	const scrollNext = useCallback(() => {
		if (mainCarouselApi && !isMobile) mainCarouselApi.scrollNext()
	}, [mainCarouselApi, isMobile])

	const onThumbClick = useCallback(
		(index: number) => {
			if (!mainCarouselApi) return
			mainCarouselApi.scrollTo(index)
			setSelectedIndex(index)
		},
		[mainCarouselApi]
	)

	const handleImageLoad = (index: number, event: any) => {
		const height = event.target.naturalHeight
		setImageHeights(prev => ({...prev, [index]: height}))
	}

	return (
		<div className='mx-auto'>
			<div className='grid grid-cols-1 md:grid-cols-[70%_27%] gap-[3%] content-start'>
				<div className='relative'>
					{/* Desktop carousel view */}
					<div className={`${isMobile ? 'hidden' : 'flex'} items-start content-start rounded-lg overflow-hidden`}>
						<div
							className='overflow-hidden relative flex-grow'
							ref={mainCarouselRef}
						>
							<div className='flex'>
								{patient.photos.map((image, index) => {
									const isSmallHeight =
										imageHeights[index] && imageHeights[index] < 800
									return (
										<div
											key={index}
											className={`flex-[0_0_100%] min-w-0 relative ${
												isSmallHeight ? 'flex items-start justify-center' : ''
											}`}
										>
											<div className='mx-auto w-[100%]'>
												<Image
													src={getReadableImageUrl(
														image.directus_files_id.id,
														image.directus_files_id.filename_disk
													)}
													width={1225}
													height={1012}
													alt={`${patient.title} before and after - View ${
														index + 1
													}`}
													className={
														isSmallHeight
															? 'w-full object-contain max-h-[90vh]'
															: 'w-full h-full object-cover'
													}
													quality={95}
													loading={'lazy'}
													onLoad={e => handleImageLoad(index, e)}
												/>
											</div>
										</div>
									)
								})}
							</div>
							
							{patient.photos.length > 1 && (
								<>
									<button 
										className="absolute left-0 top-1/2 -translate-y-1/2 w-16 h-16 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-950/10 transition-colors"
										onClick={scrollPrev}
									>
										<div className="w-7 h-7 border-l-2 border-b-2 border-white rotate-45"></div>
									</button>
									<button 
										className="absolute right-0 top-1/2 -translate-y-1/2 w-16 h-16 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-950/10 transition-colors"
										onClick={scrollNext}
									>
										<div className="w-7 h-7 border-r-2 border-t-2 border-white rotate-45"></div>
									</button>
								</>
							)}
						</div>
					</div>

					{/* Mobile horizontal scrollable view */}
					<div className={`${isMobile ? 'block' : 'hidden'} w-full h-[24rem] pb-8 overflow-x-auto bader-h-scrollbar rounded-lg`}>
						<div className="relative h-full w-[300%]">
							<Image 
								src={getReadableImageUrl(
									patient.photos[selectedIndex].directus_files_id.id,
									patient.photos[selectedIndex].directus_files_id.filename_disk
								)} 
								alt={`${patient.title} before and after - View ${selectedIndex + 1}`} 
								fill
								className="object-cover"
								sizes="150vw"
								priority
								quality={90}
							/>
						</div>
					</div>

					<div className="flex justify-between mt-2 mb-16">
						<div className="py-2">
							<span className="text-blue-950 text-lg md:text-3xl font-medium font-greycliff">BEFORE</span>
						</div>
						<div className="py-2">
							<span className="text-blue-950 text-lg md:text-3xl font-medium font-greycliff">AFTER</span>
						</div>
					</div>
					
					<div className='md:hidden flex flex-col gap-6 mb-8'>
						{patient.photos.map((image, index) => (
							<button
								key={index}
								onClick={() => onThumbClick(index)}
								className={`p-4 rounded-lg cursor-pointer transition-colors ${index === selectedIndex ? 'bg-orange-200/40' : 'bg-zinc-100 hover:bg-orange-200/20'} flex-none lg:flex-initial transition-all duration-200 relative`}
							>
								<div className="relative w-full">
									<Image
										src={getReadableImageUrl(
											image.directus_files_id.id,
											image.directus_files_id.filename_disk
										)}
										width={1225}
										height={1012}
										alt={`Thumbnail - View ${index + 1}`}
										className='w-full h-auto rounded-lg'
										quality={95}
										loading={'lazy'}
									/>
								</div>
								<div className="flex justify-between mt-2">
									<span className="text-blue-950 text-sm font-medium font-greycliff">BEFORE</span>
									<span className="text-blue-950 text-sm font-medium font-greycliff">AFTER</span>
								</div>
							</button>
						))}
					</div>
					
					<h2 className='text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-blue-950 mb-8'>
						{firstUpper(patient.title)}
					</h2>
					<p
						className='text-xl font-normal font-greycliff text-blue-950 leading-7 tracking-tight mb-8'
						dangerouslySetInnerHTML={sanitizeHTML(patient.history)}
					/>
					<br />
					<p className='text-xl font-normal font-greycliff text-blue-950 leading-7 tracking-tight mb-8'>
						*Individual results may vary
					</p>
				</div>
				<div className="hidden md:flex flex-wrap gap-6 items-start content-start">
					{patient.photos.map((image, index) => (
						<button
							key={index}
							onClick={() => onThumbClick(index)}
							className={`p-4 rounded-lg cursor-pointer transition-colors ${index === selectedIndex ? 'bg-orange-200/40' : 'bg-zinc-100 hover:bg-orange-200/20'} transition-all duration-200 relative h-fit basis-[calc(50%-0.75rem)]`}
						>
							<div className="relative w-full">
								<Image
									src={getReadableImageUrl(
										image.directus_files_id.id,
										image.directus_files_id.filename_disk
									)}
									width={1225}
									height={1012}
									alt={`Thumbnail - View ${index + 1}`}
									className="w-full h-auto rounded-lg"
									quality={95}
									loading="lazy"
								/>
							</div>
							<div className="flex justify-between mt-2">
								<span className="text-blue-950 text-sm font-medium font-greycliff">BEFORE</span>
								<span className="text-blue-950 text-sm font-medium font-greycliff">AFTER</span>
							</div>
						</button>
					))}
				</div>
			</div>
		</div>
	)
}
